(() => {
  const STORAGE_KEY = 'golfgodter-matchplay-v1';
  const FORMATS = {
    singles: { label: 'Single matchplay', players: 2, allowance: 100 },
    fourball: { label: 'Fourball matchplay', players: 4, allowance: 90 },
    foursomes: { label: 'Foursomes matchplay', players: 4, allowance: 50 }
  };

  const roundHandicap = (value) => value >= 0 ? Math.floor(value + 0.5) : Math.ceil(value - 0.5);

  const parseHandicap = (raw) => {
    const value = String(raw ?? '').trim().replace(',', '.');
    if (!value) return null;
    const plus = value.startsWith('+');
    const normalized = value.replace(/^\+/, '');
    const number = Number(normalized);
    if (!Number.isFinite(number)) return null;
    return plus ? -Math.abs(number) : number;
  };

  const formatHandicap = (value) => {
    const rounded = Math.abs(value % 1) < 0.001 ? String(Math.abs(value)) : Math.abs(value).toFixed(1).replace('.', ',');
    return value < 0 ? `+${rounded}` : rounded;
  };

  const allocationText = (strokes) => {
    if (strokes <= 0) return 'Ingen tildelte slag.';
    const full = Math.floor(strokes / 18);
    const remainder = strokes % 18;
    if (full === 0) return `1 slag på huller med stroke index 1–${remainder}.`;
    const base = `${full} ${full === 1 ? 'slag' : 'slag'} på alle 18 huller`;
    if (!remainder) return `${base}.`;
    return `${base} og yderligere 1 slag på huller med stroke index 1–${remainder}.`;
  };

  const calculate = ({ format, allowance, players }) => {
    if (!FORMATS[format]) throw new Error('Vælg en gyldig matchform.');
    if (!Number.isFinite(allowance) || allowance < 0 || allowance > 100) {
      throw new Error('Handicap allowance skal være mellem 0 og 100 %.');
    }

    const expected = FORMATS[format].players;
    if (players.length !== expected) throw new Error(`Der skal være ${expected} spillere i denne matchform.`);
    players.forEach((player, index) => {
      if (!player.name) throw new Error(`Skriv et navn til spiller ${index + 1}.`);
      if (!Number.isFinite(player.handicap) || player.handicap < -10 || player.handicap > 54) {
        throw new Error(`Indtast et gyldigt banehandicap for ${player.name || `spiller ${index + 1}`} (-10 til 54).`);
      }
    });

    if (format === 'foursomes') {
      const teams = [
        {
          id: 'A',
          name: `${players[0].name} & ${players[1].name}`,
          members: [players[0], players[1]],
          courseHandicap: players[0].handicap + players[1].handicap
        },
        {
          id: 'B',
          name: `${players[2].name} & ${players[3].name}`,
          members: [players[2], players[3]],
          courseHandicap: players[2].handicap + players[3].handicap
        }
      ];
      const base = Math.min(...teams.map((team) => team.courseHandicap));
      teams.forEach((team) => {
        team.strokes = roundHandicap((team.courseHandicap - base) * (allowance / 100));
        team.playingHandicap = team.strokes;
        team.allocation = allocationText(team.strokes);
      });
      return { format, allowance, base, entities: teams, kind: 'teams' };
    }

    const base = Math.min(...players.map((player) => player.handicap));
    const entities = players.map((player) => {
      const strokes = roundHandicap((player.handicap - base) * (allowance / 100));
      return {
        ...player,
        strokes,
        playingHandicap: strokes,
        allocation: allocationText(strokes)
      };
    });
    return { format, allowance, base, entities, kind: 'players' };
  };

  const sideForIndex = (format, index) => {
    if (format === 'singles') return index === 0 ? 'Spiller A' : 'Spiller B';
    if (format === 'fourball' || format === 'foursomes') return index < 2 ? 'Hold A' : 'Hold B';
    return '';
  };

  const defaultPlayers = (format) => Array.from({ length: FORMATS[format].players }, (_, index) => ({
    name: '',
    handicap: '',
    side: sideForIndex(format, index)
  }));

  const init = (root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const form = root.querySelector('[data-mp-form]');
    const formatOptions = root.querySelector('[data-mp-format-options]');
    const playersWrap = root.querySelector('[data-mp-players]');
    const playerTemplate = root.querySelector('[data-mp-player-template]');
    const allowanceInput = root.querySelector('[data-mp-allowance]');
    const allowanceDefault = root.querySelector('[data-mp-allowance-default]');
    const errors = root.querySelector('[data-mp-errors]');
    const results = root.querySelector('[data-mp-results]');
    const empty = root.querySelector('[data-mp-empty]');
    const resultList = root.querySelector('[data-mp-result-list]');
    const resultHeading = root.querySelector('[data-mp-result-heading]');
    const resultSummary = root.querySelector('[data-mp-result-summary]');
    const feedback = root.querySelector('[data-mp-feedback]');

    let format = 'singles';
    let lastResult = null;

    const readStored = () => {
      try {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        if (!stored || !FORMATS[stored.format]) return null;
        return stored;
      } catch (_) {
        return null;
      }
    };

    const save = () => {
      const players = Array.from(playersWrap.querySelectorAll('[data-mp-player]')).map((row) => ({
        name: row.querySelector('[data-mp-name]').value,
        handicap: row.querySelector('[data-mp-handicap]').value
      }));
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          format,
          allowance: allowanceInput.value,
          players
        }));
      } catch (_) {}
    };

    const setActiveFormat = () => {
      formatOptions.querySelectorAll('[data-mp-format]').forEach((button) => {
        const active = button.dataset.mpFormat === format;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    };

    const renderPlayers = (values = []) => {
      playersWrap.innerHTML = '';
      const defaults = defaultPlayers(format);
      defaults.forEach((player, index) => {
        const node = playerTemplate.content.firstElementChild.cloneNode(true);
        const side = node.querySelector('[data-mp-side]');
        const name = node.querySelector('[data-mp-name]');
        const handicap = node.querySelector('[data-mp-handicap]');
        side.textContent = player.side;
        name.value = values[index]?.name || '';
        handicap.value = values[index]?.handicap || '';
        name.setAttribute('aria-label', `Navn, ${player.side}`);
        handicap.setAttribute('aria-label', `Banehandicap, ${player.side}`);
        playersWrap.appendChild(node);
      });
    };

    const setFormat = (nextFormat, storedPlayers = null, storedAllowance = null) => {
      format = nextFormat;
      setActiveFormat();
      renderPlayers(storedPlayers || []);
      allowanceInput.value = storedAllowance ?? FORMATS[format].allowance;
      allowanceDefault.textContent = `Anbefalet standard: ${FORMATS[format].allowance} %`;
      errors.textContent = '';
      results.hidden = true;
      empty.hidden = false;
      lastResult = null;
      save();
    };

    const collect = () => ({
      format,
      allowance: Number(String(allowanceInput.value).replace(',', '.')),
      players: Array.from(playersWrap.querySelectorAll('[data-mp-player]')).map((row, index) => ({
        name: row.querySelector('[data-mp-name]').value.trim(),
        handicap: parseHandicap(row.querySelector('[data-mp-handicap]').value),
        side: sideForIndex(format, index)
      }))
    });

    const cardHtml = (entity) => {
      const isBase = entity.strokes === 0;
      const side = entity.id ? `Hold ${entity.id}` : entity.side;
      const course = formatHandicap(entity.courseHandicap ?? entity.handicap);
      const playing = formatHandicap(entity.playingHandicap);
      const strokeLabel = isBase ? 'Spiller fra 0' : `${entity.strokes} ${entity.strokes === 1 ? 'slag' : 'slag'}`;
      return `
        <article class="gg-matchplay__result-card${isBase ? ' gg-matchplay__result-card--base' : ''}">
          <div class="gg-matchplay__result-top">
            <div>
              <h4 class="gg-matchplay__result-name">${escapeHtml(entity.name)}</h4>
              <p class="gg-matchplay__result-side">${escapeHtml(side)}</p>
            </div>
            <span class="gg-matchplay__stroke-count">${strokeLabel}</span>
          </div>
          <p class="gg-matchplay__result-meta">Banehandicap: ${course} · Relativt spillehandicap: ${playing}</p>
          <p class="gg-matchplay__allocation">${escapeHtml(entity.allocation)}</p>
        </article>`;
    };

    const renderResult = (result) => {
      lastResult = result;
      resultHeading.textContent = FORMATS[result.format].label;
      const baseEntity = result.entities.find((entity) => entity.strokes === 0);
      resultSummary.textContent = `${baseEntity.name} har laveste spillehandicap og spiller fra 0. Beregnet med ${result.allowance} % allowance.`;
      resultList.innerHTML = result.entities.map(cardHtml).join('');
      empty.hidden = true;
      results.hidden = false;
      feedback.textContent = '';
    };

    const textResult = () => {
      if (!lastResult) return '';
      const lines = [
        `GolfGodter Matchplay-beregner`,
        `${FORMATS[lastResult.format].label} · ${lastResult.allowance} % allowance`,
        ''
      ];
      lastResult.entities.forEach((entity) => {
        const side = entity.id ? `Hold ${entity.id}` : entity.side;
        lines.push(`${side}: ${entity.name}`);
        lines.push(`Banehandicap ${formatHandicap(entity.courseHandicap ?? entity.handicap)} · Relativt spillehandicap ${formatHandicap(entity.playingHandicap)}`);
        lines.push(entity.strokes === 0 ? 'Spiller fra 0.' : `Modtager ${entity.strokes} slag. ${entity.allocation}`);
        lines.push('');
      });
      lines.push('Slag fordeles efter scorekortets stroke index. Turneringsbetingelserne gælder altid.');
      return lines.join('\n');
    };

    const escapeHtml = (value) => String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

    formatOptions.addEventListener('click', (event) => {
      const button = event.target.closest('[data-mp-format]');
      if (!button) return;
      setFormat(button.dataset.mpFormat);
    });

    playersWrap.addEventListener('input', save);
    allowanceInput.addEventListener('input', save);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      errors.textContent = '';
      try {
        const result = calculate(collect());
        renderResult(result);
        save();
        if (window.matchMedia('(max-width: 880px)').matches) {
          results.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (error) {
        errors.textContent = error instanceof Error ? error.message : 'Beregningen kunne ikke gennemføres.';
      }
    });

    root.querySelector('[data-mp-copy]').addEventListener('click', async () => {
      if (!lastResult) return;
      try {
        await navigator.clipboard.writeText(textResult());
        feedback.textContent = 'Slagfordelingen er kopieret.';
      } catch (_) {
        feedback.textContent = 'Kunne ikke kopiere automatisk. Markér resultatet manuelt.';
      }
    });

    root.querySelector('[data-mp-print]').addEventListener('click', () => {
      if (lastResult) window.print();
    });

    root.querySelector('[data-mp-reset]').addEventListener('click', () => {
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
      setFormat('singles');
      feedback.textContent = 'Beregneren er nulstillet.';
    });

    const stored = readStored();
    if (stored) {
      setFormat(stored.format, stored.players, stored.allowance);
    } else {
      setFormat('singles');
    }
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-matchplay]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
